import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Shield, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { reportError } from '@/lib/error-reporting';

interface EnhancedCoachingOptInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EnhancedCoachingOptInModal = ({
  open,
  onOpenChange,
}: EnhancedCoachingOptInModalProps) => {
  const { user } = useAuth();
  const { invalidate } = useProfile();
  const [enabling, setEnabling] = useState(false);

  const handleEnable = async () => {
    if (!user) return;
    setEnabling(true);
    try {
      await supabase
        .from('profiles')
        .update({
          enhanced_coaching_enabled: true,
          enhanced_coaching_prompted: true,
        })
        .eq('user_id', user.id);

      invalidate();
      onOpenChange(false);
    } catch (err) {
      reportError(err, 'ai-coach', { action: 'opt-in-enable' });
    } finally {
      setEnabling(false);
    }
  };

  const handleDismiss = async () => {
    if (!user) return;
    try {
      // Mark as prompted so we don't show again
      await supabase
        .from('profiles')
        .update({ enhanced_coaching_prompted: true })
        .eq('user_id', user.id);

      invalidate();
    } catch (err) {
      reportError(err, 'ai-coach', { action: 'opt-in-dismiss' });
    }
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Get coaching that remembers you
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Your AI coach can reference your goals and past conversations to give you
                better advice over time — like a real mentor who knows your story.
              </p>
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted text-sm">
                <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Your privacy matters</p>
                  <p className="text-muted-foreground mt-1">
                    Your data is never shared with other users. You can clear your coaching
                    memory or turn this off anytime in Settings.
                  </p>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleDismiss} disabled={enabling}>
            Skip for now
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleEnable} disabled={enabling}>
            {enabling ? 'Enabling...' : 'Enable Enhanced Coaching'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
